<script setup>
import { useGtm } from '@/composables/helpers/client-only/gtm';

/*
  This component accepts the following colors: white, solaris, meteor;
  If "useX" is set to true, an "X" will appear next to the text.
  If useHover is set to true, hover states will be active. Otherwise, hovering will do nothing.
  All logic should for clicks should be handled on the parent level.
*/

const props = defineProps({
    link: {
        type: [Boolean, String],
        required: false,
        default: false,
    },
    span: {
        type: Boolean,
        required: false,
        default: false,
    },
    text: {
        type: String,
        required: true,
    },
    colorSet: {
        type: String,
        required: false,
        default: 'white',
    },
    useHover: {
        type: Boolean,
        required: false,
        default: false,
    },
    useX: {
        type: Boolean,
        required: false,
        default: false,
    },
    replace: {
        type: Boolean,
        required: false,
        default: false,
    },
    ariaText: {
        type: String,
    },
    textOnly: {
        type: Boolean,
        required: false,
        default: false,        
    },
    useA: {
        type: Boolean,
        required: false,
        default: false,
    },
    gtmEventName: {
        type: String,
        required: false,
        default: 'tag_clicked',
    },
    gtmEventOptions: {
        type: [Object],
        required: false,
        default: {
            category: 'custom_events',
            action: 'click',
        },
    },
    capitalize: {
        type: Boolean,
        required: false,
        default: false,
    },
    category: {
        type: Boolean,
        required: false,
        default: false,
    }
});

const emit = defineEmits(['click']);

const handleGtmEvent = () => {
    const excludedLabels = ['Clear All Filters'];
    const shouldExclude = excludedLabels.includes(props.text);
    if (shouldExclude) return;
    const name = props.gtmEventName;
    const options = props.gtmEventOptions;
    options.label = props.text;
    useGtm(name, options);
};

const handleClick = () => {
    handleGtmEvent();
    emit('click');
};

</script>

<template>
    <MessLink v-if="link && !useA" :href="link" :replace="replace" class="project-tag"
        :class="[colorSet, { useHover: useHover, empty: textOnly, 'category': category, 'useX': useX }]" @click="handleClick()">
        <slot></slot>
        <p class="caps fs-12">
            {{ text }}
            <span v-if="ariaText" class="sr-only">{{ ariaText }}</span>
            <span></span>
        </p>
        <svg v-if="useX" aria-hidden="true" class="tag-close-icon ml-[6px] mt-px w-[7px] h-[7px] shrink-0" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.5 6.5L3.5 3.5M3.5 3.5L6.5 0.5M3.5 3.5L0.5 0.5M3.5 3.5L6.5 6.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
    </MessLink>

    <a v-else-if="link && useA" :href="link" class="mess-link not-mess-link project-tag"
        :aria-label="ariaText" 
        :class="[colorSet, { useHover: useHover, 'useX': useX }]" @click="handleClick()">
        <slot></slot>
        <p class="caps fs-12">
            {{ text }}
            <span v-if="ariaText" class="sr-only">{{ ariaText }}</span>
            <span></span>
        </p>
        <svg v-if="useX" aria-hidden="true" class="tag-close-icon ml-[6px] mt-px w-[7px] h-[7px] shrink-0" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.5 6.5L3.5 3.5M3.5 3.5L6.5 0.5M3.5 3.5L0.5 0.5M3.5 3.5L6.5 6.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
    </a>

    <span v-else-if="span" class="project-tag" :class="[colorSet, { useHover: useHover, 'useX': useX  }]" @click="handleClick()">
        <slot></slot>
        <p class="caps fs-12">
            {{ text }}
            <span v-if="ariaText" class="sr-only">{{ ariaText }}</span>
            <span></span>
        </p>
        <svg v-if="useX" aria-hidden="true" class="tag-close-icon ml-[6px] mt-px w-[7px] h-[7px] shrink-0" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.5 6.5L3.5 3.5M3.5 3.5L6.5 0.5M3.5 3.5L0.5 0.5M3.5 3.5L6.5 6.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
    </span>

    <button v-else class="project-tag" :class="[colorSet, { useHover: useHover, 'useX': useX }]" @click="handleClick()">
        <slot></slot>
        <p class="caps fs-12">
            {{ text }}
            <span v-if="ariaText" class="sr-only">{{ ariaText }}</span>
            <span></span>
        </p>
        <svg v-if="useX" aria-hidden="true" class="tag-close-icon ml-[6px] mt-px w-[7px] h-[7px] shrink-0" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.5 6.5L3.5 3.5M3.5 3.5L6.5 0.5M3.5 3.5L0.5 0.5M3.5 3.5L6.5 6.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
    </button>
</template>

<style>

a.project-tag {
    cursor: pointer;
}

.project-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--color, var(--magenta));
    background-color: var(--secondColor, transparent);
    padding: 0 0.625rem;
    border: 1px solid var(--color, var(--magenta));
    border-radius: 15px;
    transition: 0.33s ease;
    font-family: var(--font-hubspot-sans);
    font-size: 12px;
    letter-spacing: 0.24px;
    line-height: 26px;
    font-weight: 400;
    cursor: default;
    white-space: nowrap;
    text-transform: none;
}

.project-tag p {
    font-family: var(--font-hubspot-sans);
    font-size: 12px;
    letter-spacing: 0.24px;
    line-height: 26px;
    font-weight: 400;
    text-transform: none;
}

.project-tag.category {
    padding: 0.2rem 0.8rem;
    font-family: var(--font-hubspot-sans);
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.84px;
    text-transform: uppercase;
    border-radius: 50px;
    background-color: var(--magenta);
    border-color: transparent;
    color: white;
}

.project-tag.category .fs-12 {
    letter-spacing: 0.84px;
    font-weight: 500;
    text-transform: uppercase;
}

.project-tag.useHover:hover {
    cursor: pointer;
    background-color: var(--color);
    border: 1px solid var(--secondColor);
    color: var(--secondColor);
}

.project-tag.useHover.transparent:hover {
    color: var(--Chapati-500);
}

.project-tag.empty {
    padding: 0;
    border: none;
    border-radius: 0;
    border-bottom: 1px solid var(--color);
}

.project-tag.empty.useHover:hover {
    border: none;
    border-radius: 0;
    background-color: transparent;
    color: var(--orange);
    border-bottom: 1px solid var(--orange);
}

.project-tag.white {
    --color: var(--magenta);
    --secondColor: white;
    border-color: white;
}

.project-tag.white.useHover:hover {
    border-color: var(--magenta);
}

.project-tag.white .mess-svg path {
    fill: var(--magenta);
}

.project-tag.white-borderless {
    --color: var(--Neutrals-Meteor);
    --secondColor: var(--Neutrals-White);
    border-color: white;
}

.project-tag.transparent {
    --color: var(--Neutrals-Meteor);
    --secondColor: var(--Neutrals-White);
    background: transparent;
}

.project-tag.meteor {
    --color: var(--Neutrals-White);
    --secondColor: var(--Neutrals-Meteor);
}

.project-tag.meteor-transparent {
    --color: var(--magenta);
    --secondColor: var(--orange);
    background: transparent;
}

.project-tag.solaris {
    border-color: var(--orange-dark);
    --color: var(--orange-dark);
    --secondColor: transparent;
}

.project-tag.solaris:hover {
    --color: var(--cream);
    --secondColor: var(--orange);
}

.project-tag.magenta {
    border-color: var(--magenta);
    --color: var(--magenta);
    --secondColor: transparent;
}

.project-tag.magenta.useHover:hover {
    background-color: var(--magenta);
    color: white;
    border-color: var(--magenta);
}

.project-tag.magenta.useHover:hover p {
    color: white;
}

.project-tag.magenta.useHover:hover .tag-close-icon {
    color: var(--pink);
}

.project-tag.magenta-filled {
    --color: white;
    --secondColor: var(--magenta);
    border-color: var(--magenta);
    color: white;
    background-color: var(--magenta);
}

.project-tag.magenta-filled.useHover:hover {
    --color: var(--magenta);
    --secondColor: white;
    border-color: var(--magenta);
    color: var(--magenta);
    background-color: white;
}

.project-tag.white-outline {
    --color: white;
    --secondColor: transparent;
    border-color: white;
    color: white;
}

.project-tag.white-outline.useHover:hover {
    --color: var(--magenta);
    --secondColor: white;
    border-color: white;
}

.project-tag.berlin-bus {
    --color: var(--Neutrals-Eclipse);
    --secondColor: var(--Berlin-Bus-Medium);
    border: 1px solid transparent;
}

.project-tag.berlin-bus:hover {
    border: 1px solid var(--Neutrals-Eclipse);
}

.project-tag.berlin-bus-original {
    --color: var(--Neutrals-Eclipse);
    --secondColor: var(--Berlin-Bus-Original);
    border: 1px solid transparent;
}

.project-tag.berlin-bus-original:hover {
    border: 1px solid var(--Neutrals-Eclipse);
}

.project-tag.cherry-blossom {
    border-color: var(--secondColor);
    --color: var(--Neutrals-Eclipse);
    --secondColor: var(--Cherry-Blossom-Light);
}

.project-tag.cherry-blossom:hover {
    --secondColor: white;
}

.project-tag.cherry-blossom-alt {
    border-color: var(--secondColor);
    --color: var(--Neutrals-Eclipse);
    --secondColor: var(--Cherry-Blossom-Alt-Cherry-Blossom-Mid);
}

.project-tag.cherry-blossom-alt:hover {
    --secondColor: white;
}

.project-tag.solaris-alt {
    border-color: var(--secondColor);
    --color: var(--Neutrals-Eclipse);
    --secondColor: var(--Solaris-Alt-Solaris-Mid);
}

.project-tag.solaris-alt:hover {
    --secondColor: white;
}

.project-tag.calypso-alt {
    border-color: var(--secondColor);
    --color: var(--Neutrals-Eclipse);
    --secondColor: var(--Calypso-Alt-Calypso-Mid);
}

.project-tag.calypso-alt:hover {
    --secondColor: white;
}

.project-tag.neutrals-silver {
    border-color: var(--secondColor);
    --color: var(--Neutrals-Eclipse);
    --secondColor: var(--Neutrals-Silver);
}

.project-tag.neutrals-silver:hover {
    --secondColor: white;
}

.project-tag.neutrals-milky-way {
    border-color: var(--Neutrals-Milky-Way);
    --color: var(--Neutrals-Eclipse);
    --secondColor: transparent;
}

.project-tag.neutrals-milky-way:hover {
    --secondColor: white;
}

.project-tag.oz-original {
    border-color: var(--secondColor);
    --color: var(--Neutrals-Eclipse);
    --secondColor: var(--Oz-Original);
}

.project-tag.oz-original:hover {
    --secondColor: white;
}

.project-tag.calypso-light {
    border-color: var(--secondColor);
    --color: var(--Neutrals-Eclipse);
    --secondColor: var(--Calypso-Light);
}

.project-tag.calypso-light.useHover:hover {
    --secondColor: white;
}

.project-tag.transparent {
    --secondColor: var(--Neutrals-Eclipse);
    --color: var(--Neutrals-Eclipse);
    background-color: transparent;
}

.project-tag.transparent-meteor {
    --secondColor: var(--Neutrals-White);
    --color: var(--Neutrals-Meteor);
    background-color: transparent;
}

.project-tag.indigo {
    --color: var(--Neutrals-Eclipse);
    --secondColor: var(--Indigo);
    border-color: var(--Indigo);
}

.project-tag.indigo.useHover:hover {
    --secondColor: white;
}

.project-tag.blank {
    --color: var(--dark-pink);
    border-color: transparent;
    background-color: transparent;
    text-decoration: underline;
    text-underline-offset: 0.25rem;
}

.project-tag.blank.useHover:hover {
    --secondColor: var(--magenta);
    border-color: transparent;
    background-color: transparent;
}

.project-tag.clear-all,
.project-tag.clear-all p {
    --color: var(--magenta);
    color: var(--magenta);
    border-width: 0;
    background-color: transparent;
    font-family: var(--font-hubspot-serif);
    font-size: 13px;
    font-weight: 400;
    line-height: 1.4;
    letter-spacing: 0.34px;
    text-decoration: underline dotted #7D4566;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
}

.project-tag.clear-all.useHover:hover,
.project-tag.clear-all.useHover:hover p {
    border-width: 0;
    background-color: transparent;
    color: var(--magenta);
    text-decoration: none;
}

/* White variant — used on the dark-purple speakers grid where the magenta
   default disappears against the background. */
.project-tag.clear-all-white,
.project-tag.clear-all-white p {
    --color: white;
    color: white;
    border-width: 0;
    background-color: transparent;
    font-family: var(--font-hubspot-serif);
    font-size: 13px;
    font-weight: 400;
    line-height: 1.4;
    letter-spacing: 0.34px;
    text-decoration: underline dotted white;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
}

.project-tag.clear-all-white.useHover:hover,
.project-tag.clear-all-white.useHover:hover p {
    border-width: 0;
    background-color: transparent;
    color: white;
    text-decoration: none;
}

.project-tag.teal {
    --color: var(--magenta);
    --secondColor: transparent;
    border-color: var(--magenta);
}

.project-tag.teal.useHover:hover {
    --secondColor: white;
}

.project-tag.orange {
    --color: var(--orange);
    --secondColor: transparent;
    border-color: var(--orange);
}

.project-tag.orange.useHover:hover {
    --secondColor: white;
    --color: var(--orange);
}

.project-tag .mess-svg {
    align-self: center;
    margin-left: 0.75rem;
}

.project-tag.useHover:hover .mess-svg path {
    fill: white;
}

.project-tag .mess-svg.margin-right {
    margin-right: 0.75rem;
    margin-left: 0;
}

.project-tag.category.useHover:hover {
    background-color: #D63D00;
    border: 1px solid transparent;
    color: white;
}

</style>
